import {
  computeRadialLayoutResult,
  type NetworkGraphLayoutEdge,
  type NetworkGraphLayoutNode,
  type NetworkGraphLayoutSize,
  type NetworkGraphPositions,
} from './computeRadialLayout';

export interface NetworkGraphLayoutInputs {
  // Visible node/edge sets only: filtered-out members must not claim ring
  // slots or grow the chord-fit radius. Positions accumulate write-once below,
  // so a node hidden after being visible still restores its cached spot.
  layoutNodes: readonly NetworkGraphLayoutNode[];
  layoutEdges: readonly NetworkGraphLayoutEdge[];
  expandedNodeIds: readonly string[];
  size: NetworkGraphLayoutSize;
  measured: boolean;
  primaryNodeId?: string;
}

export interface NetworkGraphLayoutState {
  positions: NetworkGraphPositions;
  // Placement ray per node (degrees, anchor→node), cached write-once alongside
  // its position so labels stay aligned with the arrow the node was fanned
  // out on, even after later renders where siblings moved or were revealed.
  rayAngles: Record<string, number>;
  primaryNodeId?: string;
  // Coordinate space used by every cached and incrementally added position.
  // Later container resizes change the viewBox but do not move pinned nodes.
  layoutSize: NetworkGraphLayoutSize;
  measured: boolean;
}

export const createLayoutState = (
  inputs: NetworkGraphLayoutInputs
): NetworkGraphLayoutState => {
  const { positions, rayAngles } = computeRadialLayoutResult(
    inputs.layoutNodes,
    inputs.layoutEdges,
    {
      expandedNodeIds: inputs.expandedNodeIds,
      size: inputs.size,
      primaryNodeId: inputs.primaryNodeId,
    }
  );

  return {
    positions,
    rayAngles,
    primaryNodeId: inputs.primaryNodeId,
    layoutSize: inputs.size,
    measured: inputs.measured,
  };
};

// Positions are write-once within a coordinate space. Only the first real
// measurement and a new primary node change that space and re-fan from scratch;
// later resizes keep cached positions and place new nodes in the same space.
// Returns the same reference when nothing changed, so the caller skips setState.
export const reconcileLayoutState = (
  state: NetworkGraphLayoutState,
  inputs: NetworkGraphLayoutInputs
): NetworkGraphLayoutState => {
  const shouldReset =
    (!state.measured && inputs.measured) ||
    state.primaryNodeId !== inputs.primaryNodeId;
  if (shouldReset) {
    return createLayoutState(inputs);
  }

  const newNodeIds = inputs.layoutNodes
    .filter(node => !Object.hasOwn(state.positions, node.id))
    .map(node => node.id);
  if (newNodeIds.length === 0) {
    return state;
  }

  // Newest expansion claims new shared nodes first. The layout fans anchors in
  // expandedNodeIds order, so the reversed order is passed there as well —
  // safe here because every previously placed node is pinned, leaving only the
  // new nodes up for claim.
  const newestFirstExpandedNodeIds = [...inputs.expandedNodeIds].reverse();
  const revealed = computeRadialLayoutResult(
    inputs.layoutNodes,
    inputs.layoutEdges,
    {
      expandedNodeIds: newestFirstExpandedNodeIds,
      size: state.layoutSize,
      primaryNodeId: inputs.primaryNodeId,
      previousPositions: state.positions,
      prioritizeAroundNodeIds: newestFirstExpandedNodeIds,
      prioritizeNodeIds: newNodeIds,
    }
  );

  return {
    ...state,
    positions: { ...state.positions, ...revealed.positions },
    rayAngles: { ...state.rayAngles, ...revealed.rayAngles },
  };
};
